/* Pure helpers for the server-side multi-cost-center Actuals export. */
import moment from 'moment-timezone';
import {
  ACTUALS_EXPORT_BUSINESS_GROUP,
  ACTUALS_EXPORT_FAST_POLL_INTERVAL_MS,
  ACTUALS_EXPORT_FAST_POLL_WINDOW_MS,
  ACTUALS_EXPORT_ID_PATTERN,
  ACTUALS_EXPORT_MAX_COST_CENTERS,
  ACTUALS_EXPORT_SLOW_POLL_INTERVAL_MS
} from 'src/constants/actuals-export-constants';
import { ACTUALS_EXPORT_STATUSES } from 'src/models/ActualsExportModels';
import type { ActualsExport, ActualsExportErrorCode, ActualsExportStatus, ActualsExportTrackingState } from 'src/models/ActualsExportModels';
import type { CurrentUserAccessDetails } from 'src/models/XptUsersModel';

/** The tracking state when no export is known. */
export const EMPTY_ACTUALS_EXPORT_TRACKING_STATE: ActualsExportTrackingState = {
  exportId: null,
  status: null,
  ccCount: null,
  fileName: null,
  requestedAt: null,
  errorCode: null
};

const ERROR_CODE_PATTERN = /^[A-Z_]{1,40}$/;

const API_ERROR_MESSAGES: Record<ActualsExportErrorCode, string> = {
  INVALID_REQUEST: 'The export request is not valid. Reload the page and try again.',
  UNAUTHENTICATED: 'Your session expired. Reload the page to sign in again.',
  NOT_ENTITLED: "You don't have access to one or more of the selected cost centers.",
  ACCESS_CHANGED: 'Your access changed. Reload the page and try again.',
  NOT_FOUND: 'We could not find this export. Start a new export.',
  NOT_READY: 'The file is not ready yet. Try again in a few seconds.',
  NOT_IN_PROGRESS: 'The export is no longer running.',
  EXPORT_IN_PROGRESS: "Another export is still running. We'll download it when it's ready.",
  EXPIRED: 'The file expired. Start a new export.',
  DAILY_CAP_REACHED: "You reached today's export limit. Try again tomorrow.",
  SERVICE_DISABLED: 'Multi-cost-center export is turned off right now. Try again later.',
  ROOTS_CHANGED: 'Multi-cost-center export is paused while we check the cost center hierarchy. Try again later.',
  DEPENDENCY_FAILURE: 'We could not reach a required service. Try again later.',
  NETWORK_ERROR: 'We could not reach the export service. Check your connection and try again.',
  NOT_CONFIGURED: 'Multi-cost-center export is not available right now. Try again later.',
  INVALID_RESPONSE: 'The export service returned an unexpected response. Try again later.',
  THROTTLED: 'Too many requests. Wait a minute and try again.',
  UNKNOWN: 'Something went wrong. Try again later.'
};

const JOB_FAILURE_MESSAGES: Readonly<Record<string, string>> = {
  ACCESS_CHANGED: 'Your access changed while the file was being built.',
  SOURCE_TOO_LARGE: 'The selection is too large for one file. Choose fewer cost centers.',
  SOURCE_INTEGRITY: 'The source data failed a validation check. Contact the xPT team.',
  SOURCE_CHANGED: 'The source data was being refreshed. Try again in a few minutes.',
  SOURCE_READ_FAILED: 'We could not read the source data. Try again later.',
  LEASE_EXPIRED: 'The export took too long. Try again.',
  TIMEOUT: 'The export took too long. Try again.',
  INVOKE_FAILED: 'The export could not start. Try again.',
  EVENT_EXPIRED: 'The export could not start in time. Try again.',
  SERVICE_DISABLED: 'Multi-cost-center export was turned off. Try again later.',
  NO_DATA: 'There are no actuals for the selected cost centers.',
  INTERNAL: 'Something went wrong while building the file. Try again.'
};

const DEFAULT_JOB_FAILURE_MESSAGE = 'Something went wrong while building the file. Try again.';
const EXPIRED_JOB_MESSAGE = 'The file expired. Start a new export.';

/** The job error code of an export the user aborted. */
export const ACTUALS_EXPORT_ABORTED_JOB_CODE = 'ABORTED';

/** True while the server is still building the file. */
export const isActualsExportInProgress = (status: ActualsExportStatus | null): boolean => status === 'QUEUED' || status === 'RUNNING';

/** True for an export the user aborted (reported as FAILED with the ABORTED job error code). */
export const isAbortedActualsExport = (status: ActualsExportStatus | null, errorCode: string | null): boolean =>
  status === 'FAILED' && errorCode === ACTUALS_EXPORT_ABORTED_JOB_CODE;

export const isActualsExportStatus = (value: unknown): value is ActualsExportStatus =>
  typeof value === 'string' && (ACTUALS_EXPORT_STATUSES as readonly string[]).includes(value);

/** True for a well-formed, server-generated export ID. */
export const isValidActualsExportId = (value: unknown): value is string => typeof value === 'string' && ACTUALS_EXPORT_ID_PATTERN.test(value);

/** A safe, user-facing message for an API or client error code. */
export const getActualsExportErrorMessage = (code: ActualsExportErrorCode): string => API_ERROR_MESSAGES[code];

/** A safe, user-facing reason for a FAILED or EXPIRED job. Unknown codes get a generic message. */
export const getActualsExportFailureMessage = (status: ActualsExportStatus | null, errorCode: string | null): string => {
  if (status === 'EXPIRED') {
    return EXPIRED_JOB_MESSAGE;
  }
  return (errorCode && JOB_FAILURE_MESSAGES[errorCode]) || DEFAULT_JOB_FAILURE_MESSAGE;
};

/** "1 cost center" or "12 cost centers". */
export const formatCostCenterCount = (count: number | null): string => (count === 1 ? '1 cost center' : `${count ?? 0} cost centers`);

/** Local `HH:mm` of an ISO timestamp, or an empty string when it cannot be parsed. */
export const formatActualsExportStartTime = (isoTimestamp: string | null): string => {
  if (!isoTimestamp) {
    return '';
  }
  const parsed = moment(isoTimestamp, moment.ISO_8601, true);
  return parsed.isValid() ? parsed.local().format('HH:mm') : '';
};

/** 5 s during the first minute after the request, then 10 s. */
export const getActualsExportPollIntervalMs = (requestedAt: string | null, nowMs: number): number => {
  const requestedAtMs = requestedAt ? Date.parse(requestedAt) : Number.NaN;
  if (Number.isNaN(requestedAtMs)) {
    return ACTUALS_EXPORT_FAST_POLL_INTERVAL_MS;
  }
  return nowMs - requestedAtMs < ACTUALS_EXPORT_FAST_POLL_WINDOW_MS ? ACTUALS_EXPORT_FAST_POLL_INTERVAL_MS : ACTUALS_EXPORT_SLOW_POLL_INTERVAL_MS;
};

/** The slice payload for an export reported by the API. */
export const toActualsExportTrackingState = (actualsExport: ActualsExport): ActualsExportTrackingState => ({
  exportId: actualsExport.exportId,
  status: actualsExport.status,
  ccCount: actualsExport.costCenterCount,
  fileName: actualsExport.fileName,
  requestedAt: actualsExport.createdAt,
  errorCode: actualsExport.errorCode
});

const isNullableString = (value: unknown): value is string | null => value === null || typeof value === 'string';

/**
 * Restores a persisted tracking state, or returns null when the value is not one. localStorage is
 * writable by anything on the page, so every field is checked before it is trusted.
 */
export const parseActualsExportTrackingState = (value: unknown): ActualsExportTrackingState | null => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const candidate = value as Record<string, unknown>;
  const { exportId, status, ccCount, fileName, requestedAt, errorCode } = candidate;
  if (!isValidActualsExportId(exportId) || !isActualsExportStatus(status)) {
    return null;
  }
  const isValidCount = typeof ccCount === 'number' && Number.isInteger(ccCount) && ccCount >= 1 && ccCount <= ACTUALS_EXPORT_MAX_COST_CENTERS;
  const isValidErrorCode = errorCode === null || (typeof errorCode === 'string' && ERROR_CODE_PATTERN.test(errorCode));
  if (!isValidCount || !isNullableString(fileName) || !isNullableString(requestedAt) || !isValidErrorCode) {
    return null;
  }
  return { exportId, status, ccCount, fileName, requestedAt, errorCode };
};

/**
 * Checks a presigned download URL before the browser navigates to it: https, the exports bucket's
 * virtual-hosted us-west-2 endpoint, and the `exports/` prefix. Fails closed without a bucket name.
 */
export const isAllowedActualsExportDownloadUrl = (url: string, bucketName: string | null): boolean => {
  if (!bucketName) {
    return false;
  }
  const expectedHost = `${bucketName}.s3.us-west-2.amazonaws.com`;
  if (!url.startsWith(`https://${expectedHost}/exports/`)) {
    return false;
  }
  try {
    const parsed = new URL(url);
    return (
      parsed.protocol === 'https:' &&
      parsed.hostname === expectedHost &&
      parsed.port === '' &&
      parsed.username === '' &&
      parsed.password === '' &&
      parsed.pathname.startsWith('/exports/')
    );
  } catch {
    return false;
  }
};

/** True when the user has an access row for the business group (dev users get a row for every group). */
export const hasBusinessGroupAccess = (accessDetails: CurrentUserAccessDetails | null | undefined, businessGroupShortDesc: string): boolean =>
  accessDetails?.user_data_classifications?.some(
    (classification) => classification.data_classification_short_description === businessGroupShortDesc
  ) ?? false;

/** The multi-cost-center export is offered for S&M only, behind its flag, to users with 2 or more cost centers. */
export const isMultiCcExportAvailable = (
  businessGroupShortDesc: string | null | undefined,
  isFeatureEnabled: boolean,
  costCenterOptionCount: number
): boolean => businessGroupShortDesc === ACTUALS_EXPORT_BUSINESS_GROUP && isFeatureEnabled && costCenterOptionCount >= 2;
